import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import SearchClientsModal from '../../components/Search/SearchClientsModal.jsx';
import QuickCreateClienteModal from '../../components/clientes/QuickCreateClienteModal.jsx';
import ItemsTable from '../../components/facturacion/ItemsTable.jsx';
import ManualInvoiceTemplateModal from '../../components/facturacion/ManualInvoiceTemplateModal.jsx';
import TotalsBox from '../../components/facturacion/TotalsBox.jsx';
import SinaContextualCue from '../../components/SinaAssistant/SinaContextualCue.jsx';
import AlertDialog from '../../components/ui/alert-dialog.jsx';
import {
  computeTotals,
  discardDraft,
  getManualInvoiceConfiguration,
  issue,
  loadInvoicePdfInWindow,
  openInvoicePdfPlaceholder,
  saveDraft,
} from '../../services/invoices.js';
import { getDefaultRate } from '../../services/taxes.js';
import { getClienteById } from '../../services/clients.js';
import { evaluateManualInvoiceClient, normalizeClientForManualInvoice } from '../../services/facturacionFiscal.js';
import { printManualInvoice } from '../../lib/print/manualInvoicePrint.js';
import '../../components/facturacion/invoice.css';

function formatCurrency(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function sanitizeItem(item) {
  return {
    ...item,
    qty: Number.isFinite(Number(item?.qty)) ? Number(item?.qty) : 0,
    price: Number.isFinite(Number(item?.price)) ? Number(item?.price) : 0,
    discPct: Math.min(Math.max(Number(item?.discPct) || 0, 0), 100),
    iva: Number(item?.iva ?? getDefaultRate()) || 0,
  };
}

export function getManualInvoiceItemsValidationMessage(items = []) {
  if (!items.length) {
    return 'Agregá al menos un renglón antes de emitir.';
  }

  return null;
}

function formatCompactAddress(client) {
  return [
    client?.direccion,
    [client?.localidadNombre, client?.provinciaNombre].filter(Boolean).join(' · '),
  ]
    .filter(Boolean)
    .join(' · ');
}

function getSaleConditionLabel(value) {
  return value === 'contado' ? 'Contado' : 'Cuenta corriente';
}

function resolveClientInvoiceEmail(client) {
  const candidates = [
    client?.email,
    client?.correo,
    client?.correoElectronico,
    ...(Array.isArray(client?.emails) ? client.emails : String(client?.emails || '').split(',')),
  ];
  return candidates.map(value => String(value || '').trim()).find(value => /^\S+@\S+\.\S+$/.test(value)) || '';
}

function todayDateInput() {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}

export function getSuggestedServiceEndDate(startDate = todayDateInput()) {
  const date = new Date(`${startDate}T12:00:00`);
  date.setDate(date.getDate() + 10);
  return date.toISOString().slice(0, 10);
}

export function getManualServicePeriodValidationMessage(serviceStartDate, serviceEndDate) {
  if (!serviceStartDate || !serviceEndDate) return 'Indicá el período de prestación del servicio.';
  if (serviceEndDate < serviceStartDate) return 'La fecha hasta del servicio no puede ser anterior a la fecha desde.';
  return null;
}

function getClientSaleCondition(client) {
  const condition = String(client?.condicionPago || client?.condicionVenta || '').trim().toLowerCase();
  return condition.includes('cuenta') || condition.includes('corriente') || condition.includes('credito')
    ? 'cuenta_corriente'
    : 'contado';
}

function getSuggestedDueDate(client, saleCondition) {
  const baseDate = new Date(`${todayDateInput()}T12:00:00`);
  const days = Number(client?.diasVencimiento ?? client?.plazoPago ?? 0);
  baseDate.setDate(baseDate.getDate() + (saleCondition === 'contado' ? 0 : Math.max(days, 0)));
  return baseDate.toISOString().slice(0, 10);
}

function toNumerationDocumentType(voucherType) {
  const letter = String(voucherType || '').trim().slice(-1).toUpperCase();
  return ['A', 'B', 'C'].includes(letter) ? `FACTURA_${letter}` : '';
}

const OTHER_TAXES_TEMPLATE = [{ label: 'Otros impuestos', amount: 0 }];

function canSaveAfterIssueFailure(error) {
  const code = String(error?.code || '');
  if (code === 'AFIP_AUTHORIZED_PERSISTENCE_ERROR') return false;
  return code === 'NETWORK_ERROR' || code.startsWith('AFIP_') || Number(error?.status) >= 500;
}

export default function FacturaManual() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedSubcuentaId, setSelectedSubcuentaId] = useState('');
  const [loadingClient, setLoadingClient] = useState(false);
  const [items, setItems] = useState([]);
  const [globalDiscount, setGlobalDiscount] = useState({ mode: 'pct', value: 0 });
  const [roundToHundred, setRoundToHundred] = useState(false);
  const [otherTaxes, setOtherTaxes] = useState(() => OTHER_TAXES_TEMPLATE.map(tax => ({ ...tax })));
  const [paymentInfo, setPaymentInfo] = useState({
    saleCondition: 'contado',
    issueDate: todayDateInput(),
    dueDate: todayDateInput(),
    serviceStartDate: todayDateInput(),
    serviceEndDate: getSuggestedServiceEndDate(),
    voucherType: '',
  });
  const [salePoint, setSalePoint] = useState({ number: '', loading: false, error: '' });
  const [observations, setObservations] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isIssuing, setIsIssuing] = useState(false);
  const [emailDeliveryPrompt, setEmailDeliveryPrompt] = useState(null);
  const [failedIssueDraftPrompt, setFailedIssueDraftPrompt] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [issueResult, setIssueResult] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [resumedDraftId, setResumedDraftId] = useState(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const isMountedRef = useRef(true);

  const fiscal = useMemo(() => evaluateManualInvoiceClient(selectedClient), [selectedClient]);
  const compactAddress = useMemo(() => formatCompactAddress(fiscal.client), [fiscal.client]);
  const saleConditionLabel = useMemo(() => getSaleConditionLabel(paymentInfo.saleCondition), [paymentInfo.saleCondition]);
  const activeSubcuentas = useMemo(
    () => (selectedClient?.subcuentas || []).filter((subcuenta) => subcuenta.activo !== false),
    [selectedClient],
  );

  const totals = useMemo(
    () =>
      computeTotals({
        items: items.map(sanitizeItem),
        globalDiscPct: globalDiscount.mode === 'pct' ? globalDiscount.value : 0,
        globalDiscAmount: globalDiscount.mode === 'amount' ? globalDiscount.value : 0,
        otherTaxes,
        roundToHundred,
      }),
    [globalDiscount, items, otherTaxes, roundToHundred],
  );

  const hasItems = items.length > 0;
  const canEditItems = Boolean(selectedClient) && !fiscal.hasCriticalIssues && !issueResult;
  const canSaveDraft = Boolean(selectedClient) && !isSavingDraft && !issueResult;
  const emitButtonLabel = isIssuing
    ? 'Autorizando en AFIP...'
    : issueResult
      ? 'Factura autorizada'
      : `Emitir ${fiscal.voucherType || 'factura'} · ${formatCurrency(totals.total)}`;

  useEffect(() => {
    setPaymentInfo(prev => (prev.voucherType === fiscal.voucherType ? prev : { ...prev, voucherType: fiscal.voucherType }));
  }, [fiscal.voucherType]);

  useEffect(() => {
    const documentType = toNumerationDocumentType(fiscal.voucherType);
    if (!documentType) {
      setSalePoint({ number: '', loading: false, error: '' });
      return undefined;
    }

    let active = true;
    setSalePoint(prev => ({ ...prev, loading: true, error: '' }));
    getManualInvoiceConfiguration(documentType)
      .then(config => {
        if (!active) return;
        setSalePoint({ number: config.punto_venta_formateado || '', loading: false, error: '' });
      })
      .catch(error => {
        if (!active) return;
        setSalePoint({ number: '', loading: false, error: error.message || 'No se pudo resolver el punto de venta.' });
      });
    return () => { active = false; };
  }, [fiscal.voucherType]);

  useEffect(() => {
    if (!feedback || feedback.type !== 'success') {
      return undefined;
    }
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const resetManualDraft = useCallback(() => {
    setItems([]);
    setGlobalDiscount({ mode: 'pct', value: 0 });
    setRoundToHundred(false);
    setOtherTaxes(OTHER_TAXES_TEMPLATE.map(tax => ({ ...tax })));
    setObservations('');
    setIssueResult(null);
  }, []);

  const resetInvoiceForm = useCallback(() => {
    setIsSearchOpen(false);
    setIsCreateOpen(false);
    setSelectedClient(null);
    setSelectedSubcuentaId('');
    setLoadingClient(false);
    resetManualDraft();
    setPaymentInfo({
      saleCondition: 'contado',
      issueDate: todayDateInput(),
      dueDate: todayDateInput(),
      serviceStartDate: todayDateInput(),
      serviceEndDate: getSuggestedServiceEndDate(),
      voucherType: '',
    });
    setSalePoint({ number: '', loading: false, error: '' });
    setIsSavingDraft(false);
    setFeedback(null);
    setIsPrinting(false);
    setResumedDraftId(null);
    setIsTemplateModalOpen(false);
  }, [resetManualDraft]);

  const applyClient = useCallback(async client => {
    if (!client) {
      setSelectedClient(null);
      setSelectedSubcuentaId('');
      resetManualDraft();
      return;
    }

    const normalizedBase = normalizeClientForManualInvoice(client);
    const previousClientId = selectedClient?.id || selectedClient?.cuit || selectedClient?.razonSocial;
    const nextClientId = normalizedBase?.id || normalizedBase?.cuit || normalizedBase?.razonSocial;

    if (previousClientId && previousClientId !== nextClientId) {
      resetManualDraft();
      setSelectedSubcuentaId('');
    }

    setSelectedClient(normalizedBase);

    if (!normalizedBase?.id) {
      return;
    }

    setLoadingClient(true);
    try {
      const fullClient = await getClienteById(normalizedBase.id);
      if (fullClient) {
        const normalizedClient = normalizeClientForManualInvoice(fullClient);
        const saleCondition = getClientSaleCondition(normalizedClient);
        setSelectedClient(normalizedClient);
        setPaymentInfo(prev => ({
          ...prev,
          saleCondition,
          dueDate: getSuggestedDueDate(normalizedClient, saleCondition),
        }));
      }
    } catch (error) {
      console.error('No se pudo cargar el detalle fiscal del cliente', error);
      setFeedback({ type: 'error', message: 'No se pudo completar el detalle fiscal del cliente.' });
    } finally {
      setLoadingClient(false);
    }
  }, [resetManualDraft, selectedClient?.cuit, selectedClient?.id, selectedClient?.razonSocial]);

  useEffect(() => {
    const draft = location.state?.draft;
    if (!draft?.factura || !String(draft.factura.estado || '').toUpperCase().includes('BORR')) return;
    const savedItems = (draft.detalle || []).map((item, index) => sanitizeItem({ uid: `manual-draft-${draft.factura.id}-${index}`, codigo: item.concept_code || '', descripcion: item.descripcion || '', qty: item.cantidad, price: item.precio_unitario, iva: item.alicuota_iva, discPct: item.descuento_pct || 0, isCustom: true }));
    const draftClient = normalizeClientForManualInvoice({
      id: draft.cliente?.id || draft.factura.cliente_id,
      razonSocial: draft.cliente?.razonSocial || draft.cliente?.nombre,
      cuit: draft.cliente?.cuit,
      condicionIVA: draft.cliente?.condicionIVA || draft.cliente?.condicionIva || draft.cliente?.condicion_iva || draft.fiscal?.condicionIVA || draft.fiscal?.condicionIva || draft.fiscal?.condicion_iva,
      direccion: draft.cliente?.domicilio || draft.cliente?.direccion,
    });
    setSelectedClient(draftClient);
    setItems(savedItems);
    setObservations(draft.factura.observaciones || '');
    setResumedDraftId(Number(draft.factura.id));
    setPaymentInfo((current) => ({
      ...current,
      saleCondition: draft.factura.condicion_venta || draft.fiscal?.condicionVenta || draft.cliente?.condicionVenta || getClientSaleCondition(draftClient),
      issueDate: String(draft.factura.fecha_emision || current.issueDate).slice(0, 10),
      dueDate: String(draft.factura.fecha_vencimiento || current.dueDate).slice(0, 10),
      voucherType: `FACTURA_${draft.factura.tipo || 'B'}`,
    }));

    // The invoice detail is a historical snapshot and may omit the customer's
    // fiscal condition. Refresh the canonical client record before validating
    // the resumed draft, without replacing its saved payment condition.
    if (draftClient?.id) {
      setLoadingClient(true);
      getClienteById(draftClient.id)
        .then((client) => {
          if (isMountedRef.current && client) setSelectedClient(normalizeClientForManualInvoice(client));
        })
        .catch((error) => {
          console.error('No se pudo actualizar el detalle fiscal del cliente del borrador', error);
          if (isMountedRef.current) setFeedback({ type: 'error', message: 'No se pudo completar el detalle fiscal del cliente. Revisalo antes de emitir.' });
        })
        .finally(() => {
          if (isMountedRef.current) setLoadingClient(false);
        });
    }
    setFeedback({ type: 'info', message: 'Borrador recuperado. Revisalo y emitilo para solicitar el CAE.' });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const refreshClientId = location.state?.refreshClientId || location.state?.autoSelectClientId;
    if (!refreshClientId) {
      return;
    }

    applyClient({ id: refreshClientId });
    navigate(location.pathname, { replace: true, state: {} });
  }, [applyClient, location.pathname, location.state, navigate]);

  const handleAddConcept = () => {
    if (!canEditItems) {
      return;
    }
    setItems(prev => [
      ...prev,
      {
        uid: `manual-${Date.now()}`,
        codigo: '',
        descripcion: '',
        qty: 1,
        price: 0,
        iva: getDefaultRate(),
        discPct: 0,
        isCustom: true,
      },
    ]);
  };

  const handleItemChange = (index, nextItem) => {
    setItems(prev => prev.map((item, itemIndex) => (itemIndex === index ? sanitizeItem(nextItem) : item)));
  };

  const handleRemoveItem = index => {
    setItems(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleUseTemplateItems = (templateItems, mode) => {
    const nextItems = templateItems.map((item, index) => sanitizeItem({ uid: `manual-template-${Date.now()}-${index}`, codigo: item.codigo || '', descripcion: item.descripcion || '', qty: item.cantidad, price: item.precio_unitario, iva: item.iva, discPct: item.descuento_pct, isCustom: true }));
    setItems(previous => mode === 'append' ? [...previous, ...nextItems] : nextItems);
    setFeedback({ type: 'success', message: 'El detalle fue reutilizado como renglones editables. Los totales se recalcularon.' });
  };

  const buildPayload = () => ({
    client: fiscal.client,
    items: items.map(sanitizeItem),
    totals,
    globalDiscount,
    roundToHundred,
    otherTaxes,
    payment: {
      ...paymentInfo,
      currency: 'ARS',
      salePoint: salePoint.number,
      voucherType: fiscal.voucherType,
    },
    fecha_emision: paymentInfo.issueDate,
    observations,
    issuedAt: paymentInfo.issueDate,
    origin: 'manual',
    subcuenta_id: selectedSubcuentaId || null,
  });

  const getIssueValidationMessage = useCallback(() => {
    if (loadingClient) {
      return 'Esperá a que se complete la resolución fiscal del cliente.';
    }
    if (!selectedClient) {
      return 'Seleccioná un cliente antes de emitir la factura.';
    }
    if (fiscal.hasCriticalIssues) {
      return `Corregí ${fiscal.missingCriticalFields.join(', ')} para poder emitir la factura.`;
    }
    if (!String(fiscal.voucherType || '').trim()) {
      return 'No se pudo resolver el tipo de comprobante.';
    }
    if (salePoint.loading) {
      return 'Esperá a que se resuelva el punto de venta de la sucursal.';
    }
    if (salePoint.error || !String(salePoint.number || '').trim()) {
      return salePoint.error || 'No se pudo resolver el punto de venta configurado.';
    }
    if (!String(paymentInfo.saleCondition || '').trim()) {
      return 'Seleccioná la condición de venta.';
    }
    const servicePeriodValidationMessage = getManualServicePeriodValidationMessage(paymentInfo.serviceStartDate, paymentInfo.serviceEndDate);
    if (servicePeriodValidationMessage) return servicePeriodValidationMessage;
    const itemsValidationMessage = getManualInvoiceItemsValidationMessage(items);
    if (itemsValidationMessage) return itemsValidationMessage;

    if ((Number(totals?.total) || 0) <= 0) {
      return 'El total a emitir debe ser mayor a 0.';
    }

    return null;
  }, [fiscal.hasCriticalIssues, fiscal.missingCriticalFields, fiscal.voucherType, items, loadingClient, paymentInfo.saleCondition, paymentInfo.serviceEndDate, paymentInfo.serviceStartDate, salePoint.error, salePoint.loading, salePoint.number, selectedClient, totals?.total]);

  const handleSaveDraft = async () => {
    if (!canSaveDraft) {
      return;
    }
    setFeedback(null);
    setIsSavingDraft(true);
    try {
      const response = await saveDraft(buildPayload());
      if (!response?.ok) {
        throw new Error(response?.message || 'Draft rejected');
      }
      setFeedback({ type: 'success', message: response?.message || `Borrador guardado (#${response.draftId || 'N/A'})` });
    } catch (error) {
      console.error('Error al guardar borrador manual', error);
      setFeedback({ type: 'error', message: error?.message || 'No se pudo guardar el borrador.' });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleEmitirFactura = async () => {
    if (isIssuing) {
      return;
    }

    const validationMessage = getIssueValidationMessage();
    if (validationMessage) {
      setFeedback({ type: 'error', message: validationMessage });
      return;
    }

    // This must stay synchronous with the click: opening it after `await issue()`
    // would allow popup blockers to reject the authoritative invoice PDF.
    const invoicePdfWindow = openInvoicePdfPlaceholder();
    setFeedback({ type: 'info', message: 'Validando datos antes de emitir…' });
    setIsIssuing(true);
    try {
      const response = await issue(buildPayload());
      setIssueResult(response);
      if (!response?.ok) {
        throw new Error(response?.message || 'Issue rejected');
      }
      setFeedback({
        type: 'success',
        message: response?.message || `Factura emitida (${response.number || 'sin número'})`,
      });
      if (resumedDraftId) {
        try {
          await discardDraft(resumedDraftId);
          setResumedDraftId(null);
        } catch (discardError) {
          console.error('La factura fue emitida, pero no se pudo descartar el borrador original', discardError);
          setFeedback({ type: 'info', message: `Factura emitida. El borrador original sigue disponible y podés descartarlo desde el listado.` });
        }
      }
      const clientEmail = resolveClientInvoiceEmail(fiscal.client);
      if (clientEmail) {
        setEmailDeliveryPrompt({ invoiceNumber: response.number || 'emitida', email: clientEmail });
      } else {
        setFeedback({
          type: 'info',
          message: 'Factura autorizada. El cliente no tiene un email válido registrado para ofrecer el envío.',
        });
      }
      try {
        await loadInvoicePdfInWindow(response.facturaId, invoicePdfWindow);
      } catch (pdfError) {
        console.error('La factura fue emitida, pero no se pudo abrir su PDF', pdfError);
      }
      resetInvoiceForm();
    } catch (error) {
      console.error('Error al emitir factura manual', error);
      invoicePdfWindow?.close();
      setIssueResult(null);
      const issueMessage = error?.message || 'No se pudo emitir la factura.';
      if (canSaveAfterIssueFailure(error)) {
        setFeedback({ type: 'error', message: issueMessage });
        setFailedIssueDraftPrompt({ issueMessage });
        return;
      }
      setFeedback({ type: 'error', message: issueMessage });
    } finally {
      setIsIssuing(false);
    }
  };

  const handleSaveFailedIssueAsDraft = async () => {
    const issueMessage = failedIssueDraftPrompt?.issueMessage || 'No se pudo emitir la factura.';
    setIsSavingDraft(true);
    try {
      const draft = await saveDraft(buildPayload());
      setFeedback({
        type: 'success',
        message: draft?.message || `Borrador guardado (#${draft?.draftId || 'N/A'}).`,
      });
    } catch (draftError) {
      console.error('Error al guardar el borrador tras fallar la emisión manual', draftError);
      setFeedback({
        type: 'error',
        message: `${issueMessage} Además, no se pudo guardar el borrador: ${draftError?.message || 'error desconocido.'}`,
      });
      throw draftError;
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePrintInvoice = async () => {
    if (!issueResult || isPrinting) return;
    setIsPrinting(true);
    try {
      await printManualInvoice({
        client: fiscal.client,
        items: items.map(sanitizeItem),
        totals,
        payment: { ...paymentInfo, salePoint: salePoint.number, voucherType: fiscal.voucherType },
        observations,
        issuedAt: new Date().toISOString(),
        result: issueResult,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error?.message || 'No se pudo abrir el comprobante para imprimir.' });
    } finally {
      setIsPrinting(false);
    }
  };

  const issueValidationMessage = getIssueValidationMessage();
  const canIssue = !isIssuing && !loadingClient && !issueResult && !issueValidationMessage;

  return (
    <div className="space-y-4 invoice-manual-page">
      <PageHeader
        title="Factura manual"
        subtitle="Operá la carga manual en formato compacto, con total visible y emisión centralizada."
      />

      {issueResult || feedback?.type === 'error' ? (
        <section className="invoice-manual-success" aria-live="polite">
          {issueResult ? (
            <SinaContextualCue
              state="success"
              title="¡Factura autorizada!"
              message={`La factura ${issueResult.number || 'emitida'} quedó autorizada por AFIP. CAE ${issueResult.cae || 'pendiente'} · Vto ${issueResult.caeVto || 'sin informar'}.`}
              className="invoice-manual-success__cue"
            />
          ) : (
            <SinaContextualCue
              state="error"
              message={feedback.message}
              className="invoice-manual-success__cue"
            />
          )}
        </section>
      ) : null}

      <section className="invoice-manual-summarybar">
        <div className="invoice-manual-summarybar__content">
          {!selectedClient ? (
            <div className="invoice-manual-summarybar__empty">
              <div>
                <strong>Seleccioná un cliente para comenzar la factura</strong>
                <span>Al elegirlo, se resolverán los datos fiscales y se habilitará el detalle manual.</span>
              </div>
            </div>
          ) : (
            <>
              <div className="invoice-manual-summarybar__primary">
                <div className="invoice-manual-summarybar__client">
                  <span className="invoice-manual-chip-label">Cliente</span>
                  <strong>{fiscal.client?.razonSocial || 'Sin razón social'}</strong>
                </div>
                <div className="invoice-manual-summarybar__status">
                  <span className={`invoice-manual-statuschip${fiscal.hasCriticalIssues ? ' is-danger' : ''}`}>
                    {loadingClient ? 'Resolviendo…' : fiscal.voucherType || 'Pendiente'}
                  </span>
                  <span className={`invoice-manual-statuschip${fiscal.hasCriticalIssues ? ' is-danger' : ''}`}>
                    {issueResult ? 'Autorizada' : fiscal.hasCriticalIssues ? 'Requiere corrección' : 'Listo para emitir'}
                  </span>
                </div>
              </div>

              <div className="invoice-manual-summarybar__meta">
                <div>
                  <span className="invoice-manual-chip-label">CUIT / DNI</span>
                  <strong>{fiscal.client?.cuit || '—'}</strong>
                </div>
                <div>
                  <span className="invoice-manual-chip-label">Condición IVA</span>
                  <strong>{fiscal.client?.condicionIVA || '—'}</strong>
                </div>
                <div>
                  <span className="invoice-manual-chip-label">Condición comercial</span>
                  <strong>{saleConditionLabel}</strong>
                </div>
                <div>
                  <span className="invoice-manual-chip-label">Domicilio útil</span>
                  <strong>{compactAddress || '—'}</strong>
                </div>
              </div>

              <div className={`invoice-manual-inline-alert${fiscal.hasCriticalIssues ? ' is-danger' : ''}`}>
                <strong>{fiscal.voucherType || 'Comprobante pendiente'}</strong>
                <span>
                  {fiscal.hasCriticalIssues
                    ? `Completá ${fiscal.missingCriticalFields.join(', ')} antes de continuar.`
                    : issueResult
                      ? `Comprobante ${issueResult.number} · CAE ${issueResult.cae} · Vto ${issueResult.caeVto}`
                      : fiscal.voucherReason}
                </span>
              </div>
            </>
          )}
        </div>

        {!issueResult ? <div className="invoice-manual-summarybar__actions">
          <div className="invoice-manual-action-group">
            <span>Acciones del cliente</span>
            <div>
              <button type="button" className="btn btn--secondary" onClick={() => setIsSearchOpen(true)}>
                {selectedClient ? 'Cambiar cliente' : 'Buscar cliente'}
              </button>
              {selectedClient?.id ? (
                <button type="button" className="btn btn--secondary" onClick={() => navigate(`/admin/clientes/${selectedClient.id}`, { state: { returnTo: location.pathname } })}>
                  Corregir cliente
                </button>
              ) : null}
              <button type="button" className="btn btn--secondary" onClick={() => setIsCreateOpen(true)}>Crear cliente</button>
              <button type="button" className="btn btn--secondary" onClick={() => navigate('/clientes/nuevo', { state: { returnTo: location.pathname } })}>Alta completa</button>
            </div>
          </div>
        </div> : null}
      </section>

      <section className="invoice-manual-editor">
        <div className="invoice-manual-editor__main">
          {!canEditItems ? (
            <div className="invoice-card">
              <div className="invoice-card__header">
                <h2 className="invoice-card__title">Detalle manual</h2>
              </div>
              <div className="invoice-empty">
                {selectedClient
                  ? 'Corregí los datos fiscales del cliente para habilitar la carga manual de conceptos.'
                  : 'Después de elegir cliente se habilita el editor de conceptos manuales.'}
              </div>
            </div>
          ) : (
            <>
            <ItemsTable
              items={items}
              onItemChange={handleItemChange}
              onRemoveItem={handleRemoveItem}
              onAddConceptClick={handleAddConcept}
              secondaryActionLabel="Traer detalle de otra factura"
              onSecondaryActionClick={() => setIsTemplateModalOpen(true)}
              autoFocusNewItemField="descripcion"
              manualEntry
              title="Conceptos manuales"
              emptyMessage="Todavía no agregaste conceptos a la factura manual."
              addConceptLabel="Agregar renglón"
              showProductButton={false}
            />
            </>
          )}

          <div className="invoice-card">
            <div className="invoice-card__header">
              <h3 className="invoice-card__title">Observaciones</h3>
            </div>
            <div className="invoice-card__body">
              <textarea
                className="invoice-textarea"
                value={observations}
                onChange={event => setObservations(event.target.value)}
                placeholder="Texto fiscal o comercial complementario de esta factura manual."
              />
            </div>
          </div>
        </div>

        <aside className="invoice-manual-editor__aside">
          <div className={`invoice-card invoice-manual-controlpanel${issueResult ? ' is-authorized' : ''}`}>
            <div className="invoice-card__header">
              <div>
                <h3 className="invoice-card__title">Control de emisión</h3>
                <p className="invoice-totals__notes">Comprobante, totales y acciones en una sola vista.</p>
              </div>
              <span className="invoice-manual-controlpanel__total">{formatCurrency(totals.total)}</span>
            </div>

            <div className="invoice-manual-controlpanel__section">
              <div className="invoice-manual-sectionhead">
                <span>Datos fiscales de emisión</span>
                <small>Información resuelta para este comprobante.</small>
              </div>
              <div className="invoice-manual-fiscal-summary">
                <div>
                  <span>Tipo de comprobante</span>
                  <strong>{fiscal.voucherType || 'Pendiente'}</strong>
                </div>
                <label>
                  <span>Condición de venta</span>
                  <select
                    value={paymentInfo.saleCondition}
                    onChange={event => {
                      const saleCondition = event.target.value;
                      setPaymentInfo(prev => ({ ...prev, saleCondition, dueDate: saleCondition === 'contado' ? todayDateInput() : prev.dueDate }));
                    }}
                  >
                    <option value="contado">Contado</option>
                    <option value="cuenta_corriente">Cuenta corriente</option>
                  </select>
                </label>
                {activeSubcuentas.length > 0 ? <label>
                  <span>Subcuenta</span>
                  <select value={selectedSubcuentaId} onChange={event => setSelectedSubcuentaId(event.target.value)}>
                    <option value="">Cliente principal</option>
                    {activeSubcuentas.map((subcuenta) => <option key={subcuenta.id} value={subcuenta.id}>{subcuenta.codigo} · {subcuenta.nombre}</option>)}
                  </select>
                </label> : null}
                <label>
                  <span>Fecha de emisión</span>
                  <input
                    type="date"
                    value={paymentInfo.issueDate}
                    onChange={event => {
                      const issueDate = event.target.value;
                      setPaymentInfo(prev => ({
                        ...prev,
                        issueDate,
                        dueDate: !prev.dueDate || (issueDate && prev.dueDate < issueDate) ? issueDate : prev.dueDate,
                      }));
                    }}
                  />
                </label>
                <label>
                  <span>Fecha de vencimiento</span>
                  <input type="date" value={paymentInfo.dueDate} onChange={event => setPaymentInfo(prev => ({ ...prev, dueDate: event.target.value }))} />
                </label>
                <label>
                  <span>Servicio desde</span>
                  <input
                    type="date"
                    value={paymentInfo.serviceStartDate}
                    onChange={event => setPaymentInfo(prev => ({
                      ...prev,
                      serviceStartDate: event.target.value,
                      serviceEndDate: !prev.serviceEndDate || event.target.value > prev.serviceEndDate ? event.target.value : prev.serviceEndDate,
                    }))}
                  />
                </label>
                <label>
                  <span>Servicio hasta</span>
                  <input type="date" value={paymentInfo.serviceEndDate} onChange={event => setPaymentInfo(prev => ({ ...prev, serviceEndDate: event.target.value }))} />
                </label>
              </div>
              <p className="invoice-manual-fiscal-meta">
                {fiscal.voucherType || 'Comprobante pendiente'} · PV {salePoint.loading ? 'resolviendo…' : salePoint.number || 'sin configurar'}
              </p>
            </div>

        <TotalsBox
              totals={totals}
              globalDiscount={globalDiscount}
              onGlobalDiscountChange={setGlobalDiscount}
              otherTaxes={otherTaxes}
          onOtherTaxesChange={setOtherTaxes}
          roundToHundred={roundToHundred}
          onRoundToHundredChange={setRoundToHundred}
              editable={canEditItems}
              embedded
              className="invoice-manual-controlpanel__totals"
            />

            <div className="invoice-manual-controlpanel__section">
              <div className="invoice-manual-sectionhead">
                <span>Totales y estado</span>
                <small>Resumen compacto para validar antes de emitir.</small>
              </div>
              <div className="invoice-manual-quickstats">
                <div className="invoice-totals__row">
                  <span>Renglones</span>
                  <strong>{items.length}</strong>
                </div>
                <div className="invoice-totals__row">
                  <span>Neto</span>
                  <strong>{formatCurrency(totals.net)}</strong>
                </div>
                <div className="invoice-totals__row" data-emphasis="true">
                  <span>Total</span>
                  <strong>{formatCurrency(totals.total)}</strong>
                </div>
              </div>

              {!issueResult ? (
                <div className="invoice-manual-controlpanel__footer">
                  <div className="invoice-manual-controlpanel__actions">
                    <button
                      type="button"
                      className="btn btn--primary invoice-manual-controlpanel__emit"
                      disabled={!canIssue}
                      onClick={handleEmitirFactura}
                      title={canIssue ? 'Emitir factura manual en AFIP' : issueValidationMessage || 'No se puede emitir todavía'}
                    >
                      {emitButtonLabel}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary invoice-manual-controlpanel__draft"
                      disabled={!canSaveDraft}
                      onClick={handleSaveDraft}
                    >
                      {isSavingDraft ? 'Guardando...' : 'Guardar borrador'}
                    </button>
                  </div>
                </div>
              ) : null}

              {!hasItems ? (
                <div className="invoice-manual-helper">
                  Los totales se recalculan en vivo cuando agregás renglones manuales.
                </div>
              ) : null}

              {feedback && feedback.type !== 'error' ? (
                <div className={`invoice-manual-inline-alert is-${feedback.type}`}>
                  <strong>
                    {feedback.type === 'success'
                      ? 'Emisión OK'
                      : feedback.type === 'error'
                        ? 'Revisá la factura'
                        : 'Estado'}
                  </strong>
                  <span>{feedback.message}</span>
                </div>
              ) : null}

              {issueResult ? (
                <div className="invoice-manual-inline-alert is-success">
                  <strong>Factura autorizada</strong>
                  <span>
                    {issueResult.number} · CAE {issueResult.cae} · Vto {issueResult.caeVto}
                  </span>
                </div>
              ) : null}

              {issueResult ? (
                <button type="button" className="btn btn--primary invoice-manual-controlpanel__emit" onClick={handlePrintInvoice} disabled={isPrinting}>
                  {isPrinting ? 'Generando comprobante...' : 'Imprimir / guardar PDF'}
                </button>
              ) : null}
            </div>

            {issueValidationMessage && !issueResult ? <div className="invoice-manual-helper invoice-manual-helper--warning">{issueValidationMessage}</div> : null}
          </div>
        </aside>
      </section>

      <ManualInvoiceTemplateModal isOpen={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)} clientId={selectedClient?.id} hasItems={items.length > 0} onUse={handleUseTemplateItems} />

      <SearchClientsModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelect={client => {
          setIsSearchOpen(false);
          applyClient(client);
        }}
      />

      <QuickCreateClienteModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onComplete={client => {
          setIsCreateOpen(false);
          applyClient(client);
        }}
      />
      <AlertDialog
        open={Boolean(emailDeliveryPrompt)}
        onOpenChange={(open) => { if (!open) setEmailDeliveryPrompt(null); }}
        title="Enviar factura por email"
        description={emailDeliveryPrompt ? `La factura ${emailDeliveryPrompt.invoiceNumber} fue autorizada. ¿Querés enviarla por email a ${emailDeliveryPrompt.email}?` : ''}
        confirmLabel="Enviar por email"
        cancelLabel="Ahora no"
        pendingLabel="Solicitando envío…"
        onConfirm={async () => {
          setFeedback({ type: 'info', message: `Se solicitó el envío a ${emailDeliveryPrompt.email}. Por ahora la decisión queda confirmada; el envío automático se conectará con la configuración de correo del tenant.` });
        }}
      />
      <AlertDialog
        open={Boolean(failedIssueDraftPrompt)}
        onOpenChange={(open) => { if (!open) setFailedIssueDraftPrompt(null); }}
        title="Guardar como borrador"
        description="La factura no fue confirmada como autorizada. ¿Querés guardarla como borrador para revisarla y autorizarla más tarde?"
        confirmLabel="Guardar borrador"
        cancelLabel="Cancelar"
        pendingLabel="Guardando…"
        onConfirm={handleSaveFailedIssueAsDraft}
      >
        <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-100">{failedIssueDraftPrompt?.issueMessage}</p>
      </AlertDialog>
    </div>
  );
}
